import {ActionMenu, Button, CounterLabel, NavList} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Link} from '@github-ui/react-core/link'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'
import {useHeaderTabsWithCounts} from './header-tabs/header-tabs'
import type {HeaderTab} from './header-tabs/types'
import type {HeaderRepository} from '../page-data/payloads/header'
import type {PullRequest} from '../types'
import type {NavigationUrls} from '../types/navigation-urls-types'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import styles from './MiniHeaderTabSwitcher.module.css'

export type MiniHeaderTabSwitcherProps = {
  activeRoute: PullRequestActiveRoute
  pullRequest: PullRequest
  repository: HeaderRepository
  urls: NavigationUrls
}

/**
 * Sticky-mini-header tab switcher. Renders an icon-only trigger whose icon mirrors the
 * active tab, opening a Primer NavList menu that lists every available PR tab in the same
 * order as the underline nav.
 */
export function MiniHeaderTabSwitcher({activeRoute, pullRequest, repository, urls}: MiniHeaderTabSwitcherProps) {
  const tabs = useHeaderTabsWithCounts({pullRequest, repository, urls})
  const [open, setOpen] = useState(false)
  const closeMenu = useCallback(() => setOpen(false), [])

  const activeTab = tabs.find(tab => tab.key === activeRoute) ?? tabs[0]
  if (!activeTab) return null

  const ActiveIcon = activeTab.icon

  return (
    <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
      <ActionMenu open={open} onOpenChange={setOpen}>
        <ActionMenu.Anchor>
          <Button
            variant="invisible"
            size="medium"
            className={clsx(styles.trigger, styles.accent)}
            aria-label={`Switch tab (current: ${activeTab.label})`}
            data-testid="mini-header-tab-switcher-trigger"
          >
            <span className={styles.triggerContent}>
              <ActiveIcon />
              <TriangleDownIcon />
            </span>
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align="start">
          <NavList aria-label="Pull request navigation (compact)">
            {tabs.map(tab => (
              <MiniHeaderTabSwitcherItem
                key={tab.key}
                tab={tab}
                isActive={tab.key === activeRoute}
                onNavigate={closeMenu}
              />
            ))}
          </NavList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </FeatureFlags>
  )
}

function MiniHeaderTabSwitcherItem({
  tab,
  isActive,
  onNavigate,
}: {
  tab: HeaderTab
  isActive: boolean
  onNavigate: () => void
}) {
  const Icon = tab.icon
  const ariaCurrent: 'page' | undefined = isActive ? 'page' : undefined
  const counter =
    tab.count !== undefined ? (
      <CounterLabel className={clsx(tab.key !== 'commits' && tab.countLoading ? styles.counterLoading : undefined)}>
        {`${tab.count}${tab.countLimitExceeded ? '+' : ''}`}
      </CounterLabel>
    ) : null

  // Don't set headerNavId because it's an anchor id used by non-React scroll
  // behavior and must remain unique on the page.
  if (tab.kind === 'router') {
    return (
      <NavList.Item as={Link} to={tab.to} aria-current={ariaCurrent} onClick={onNavigate}>
        <NavList.LeadingVisual>
          <Icon />
        </NavList.LeadingVisual>
        {tab.label}
        {counter && <NavList.TrailingVisual>{counter}</NavList.TrailingVisual>}
      </NavList.Item>
    )
  }

  return (
    <NavList.Item href={tab.href} data-prefetch={tab.prefetch} aria-current={ariaCurrent} onClick={onNavigate}>
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {tab.label}
      {counter && <NavList.TrailingVisual>{counter}</NavList.TrailingVisual>}
    </NavList.Item>
  )
}
